import React from 'react';
import Icon from 'components/canvas/components/icon';
import Delete from 'components/canvas/components/nodes/components/base-node/components/node-context-menu/icons/delete.svg';
import DeleteGold from 'components/canvas/components/nodes/components/base-node/components/node-context-menu/icons/delete-red.svg';
import { useTranslation } from 'i18n';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useSoftDeleteSurfaceNodeMutation from 'graphql/hooks/use-soft-delete-surface-node-mutation';
import useDeleteSurfaceNodeMutation from 'graphql/hooks/use-delete-surface-node-mutation';
import { useCanvasClipboardOperations } from 'state/canvas-clipboard';
import { useSelectedNodeOperations } from 'state/canvas/selected-node';
import surfaceNodeWithReferences from 'graphql/surface-node/queries/surface-node-with-references';
import { toast } from 'services/toast';
import { SurfaceNodeUnion } from 'graphql/types';

export interface DeleteIconProps {
  node?: SurfaceNodeUnion;
  onClick?: any;
  onAfterDelete?: Function;
  hardDelete?: boolean;
  isSelected?: boolean;
  width?: number;
  height?: number;
}

const defaultProps = {
  hardDelete: false,
};

function DeleteIcon(props: DeleteIconProps) {
  const { node } = props;
  const surfaceId = node?.surface?.id ?? '';

  const { t } = useTranslation();
  const { clearSelectedNode } = useSelectedNodeOperations();
  const { deleteConfirmModalRef } = useCanvas();
  const { removeNodeFromClipboard } = useCanvasClipboardOperations();

  const softDeleteSurfaceNode = useSoftDeleteSurfaceNodeMutation(surfaceId);

  const deleteSurfaceNode = useDeleteSurfaceNodeMutation(surfaceId);

  const [isSelected, setSelected] = React.useState<boolean>(false);

  const src = isSelected ? DeleteGold : Delete;

  function deleteNode() {
    if (node) {
      const operation = props.hardDelete
        ? deleteSurfaceNode
        : softDeleteSurfaceNode;

      return operation({ variables: { id: node.id } }).then((response) => {
        if (response.isFailure) {
          return toast.error(response.getErrorMessage());
        }

        props.onAfterDelete && props.onAfterDelete();

        clearSelectedNode();
        removeNodeFromClipboard(node.id);

        toast.success(
          t(`Successfully deleted node {{name}}`, {
            replace: { name: node?.name || '' },
          }),
        );

        return response;
      });
    }
  }

  async function showDeleteConfirmation() {
    if (node) {
      deleteConfirmModalRef.current?.setIsLoading(true);
      deleteConfirmModalRef.current?.show();

      let references = [];

      try {
        const response = await surfaceNodeWithReferences(
          { id: node.id },
          { fetchPolicy: 'network-only' },
        );

        if (response.isFailure) {
          deleteConfirmModalRef.current?.hide();
          deleteConfirmModalRef.current?.setIsLoading(false);
          toast.error(response.getErrorMessage());
          return;
        }

        references = response.getData().data?.references;
      } catch (error) {
        deleteConfirmModalRef.current?.hide();
        deleteConfirmModalRef.current?.setIsLoading(false);
        toast.error(
          t('Something went wrong, could not complete the operation'),
        );
        return;
      }

      deleteConfirmModalRef.current?.setData({
        name: node.name,
        additionalText:
          references && references.length > 0
            ? t(
                'Node {{name}} contain references that will be deleted. Do you want to proceed?',
                {
                  replace: { name: node.name },
                },
              )
            : '',
        onConfirmHandler: deleteNode,
      });

      deleteConfirmModalRef.current?.setIsLoading(false);
    }
  }

  function onClick(event) {
    event.cancleBubble = true;

    if (node) {
      showDeleteConfirmation();
    }

    props.onClick && props.onClick(event);
  }

  return (
    <Icon
      {...props}
      src={src}
      onClick={onClick}
      onMouseOver={() => setSelected(true)}
      onMouseOut={() => setSelected(false)}
    />
  );
}

DeleteIcon.defaultProps = defaultProps;

export default DeleteIcon;
